import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { captureException } from '@sentry/vue';
import {
  debounceTime,
  Subject,
  distinctUntilChanged,
  throttleTime,
  map,
  share,
} from 'rxjs';
import { uniqBy } from 'lodash';
import { useI18n } from 'vue-i18n';

import {
  useEditingIngredientStore,
  useGlobalCurrencyStore,
  useItineraryStore,
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { mapLanguageLocale } from '@apps/main/modules/localization/localeUtils';
import type { MapComponent } from '@apps/main/new-desktop/modules/maps';
import { MarkerType } from '@apps/main/modules/maps/markerFormatters';
import {
  isFlightIngredient,
  isHotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import { isOwnTransportIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';

import {
  createPolygonFromRectangle,
  DirectionsService,
  GeocodingService,
  getConnectionType,
  MapTypeId,
} from '@libs/maps';
import type {
  Map,
  Coordinate,
  PlaceId,
  LocationCriteria,
  Location,
  Rectangle,
  Marker,
  RouteWithDetails,
  Route,
  RouteConnection,
} from '@libs/maps';
import type {
  AvailableTransportIngredientResponseWithStaticInfo,
  DestinationStaticInfo,
  IngredientId,
  OverviewDestinationStaticInfo,
  OwnStayIngredientResponseWithStaticInfo,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationType,
  ConnectionType,
  MyPositionType,
  TransportType,
} from '@libs/trip-planner-interfaces';
import { PolygonConstraint } from '@libs/constraint-builder';
import {
  asObservable,
  assertValue,
  getConnectionTypeFromOwnTransportType,
  isBookableTransport,
  isGolfActivity,
  isTicketActivity,
  logged,
  readonlyRef,
  stringComparer,
} from '@libs/utils';
import {
  isAvailableHotelIngredientResponseWithStaticInfo,
  isAvailableTransportIngredientResponseWithStaticInfo,
  isFerryIngredientResponse,
  isFlightIngredientResponse,
  isOwnStayIngredientResponseWithStaticInfo,
  isOwnTransportIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';

import { useConfigurationPanelStore } from '../configurationPanel/configurationPanelStore';
import { useRecipeStore } from '../recipe/recipeStore';
import { TripPlannerPage } from '../../models';

import { getPrimaryMapMarkers, getMarkerFormatters } from './mapStoreLogic';

// Interfaces and types
export interface MapMovedEvent {
  newView: Rectangle;
  userInitiated: boolean;
}

export interface LocationMarker {
  key: string;
  position: Coordinate;
  data: {
    isLocation: boolean;
    connectionToBeReached: ConnectionType;
    positionType: MyPositionType;
  };
  title: string;
  subtitle: string | undefined;
  useContentFormatter: MarkerType;
  displayPriority: number;
}

export interface MarkerData {
  isDestination: boolean;
  isLocation: boolean;
  hotelCode: string;
  ingredientId: IngredientId;
  typeOfProduct: AccommodationType;
  semboSunRating?: number;
  connectionToBeReached?: ConnectionType;
}

export type MapProperties = InstanceType<typeof MapComponent>['$props'];

export type MapStoreReturn = ReturnType<typeof useMapStore>;

export const useMapStore = defineStore('MapStore', () => {
  const tripStore = useTripStore();
  const itineraryStore = useItineraryStore();
  const statsigEventsStore = useStatsigEventsStore();
  const configurationPanelStore = useConfigurationPanelStore();
  const geocodingService = new GeocodingService(mapLanguageLocale);
  const directionsService = new DirectionsService(mapLanguageLocale);
  const recipeStore = useRecipeStore();
  const editingIngredientStore = useEditingIngredientStore();
  const globalCurrencyStore = useGlobalCurrencyStore();
  const routerStore = useRouterStore();
  const { t } = useI18n();

  const DEFAULT_RECTANGLE: Rectangle = {
    topLeft: {
      longitude: -7.38,
      latitude: 61.225,
    },
    bottomRight: {
      longitude: 31.64,
      latitude: 46.115,
    },
  };
  const mapViewDebounceTime = 100;
  const throttleTimeMiliseconds = 1000;

  // State
  const isMapCreated = ref<boolean>(false);
  const isFirstMapRendered = ref<boolean>(true);
  const shouldShowFullScreenMap = ref<boolean>(false);
  const mapTypeId = ref<MapTypeId>(MapTypeId.ROADMAP);
  const searchByMapMovement = ref(true);
  const calculatedRoute = ref<RouteWithDetails>([]);
  const mapShouldCenterOnMarkers = ref(true);
  const isLoadingCalculatedRoute = ref<boolean>(true);
  const mapIntegration = ref<Map>();
  const mapMovedByUser = ref(false);
  const mapViews = new Subject<PolygonConstraint>();
  const startingLocation = ref<Location>();
  const tripMarkers = ref<Marker[]>([]);
  const endLocation = ref<Location>();
  const polygonHotelConstraint = ref<PolygonConstraint>();
  const polygonActivityConstraint = ref<PolygonConstraint>();

  const startingLocationMarker = computed(() => {
    if (!startingLocation.value) {
      return undefined;
    }

    return createLocationMarker(
      MyPositionType.Start,
      startingLocation.value,
      ConnectionType.NONE,
      routerStore.getCurrentTripPlannerView() === TripPlannerPage.CarTrip
    );
  });

  const endLocationMarker = computed(() => {
    if (!endLocation.value) {
      return undefined;
    }

    return createLocationMarker(
      MyPositionType.End,
      endLocation.value,
      ConnectionType.NONE,
      routerStore.getCurrentTripPlannerView() === TripPlannerPage.CarTrip
    );
  });

  // Actions
  function createLocationMarker(
    point: MyPositionType,
    location: Location,
    connectionToBeReached: ConnectionType = ConnectionType.NONE,
    isCarTrip = false
  ): LocationMarker {
    const useContentFormatter = getLocationMarkerFormatter(location, isCarTrip);

    return {
      key: `${point}-location-${location.position.latitude}-${location.position.longitude}`,
      position: location.position,
      data: { isLocation: true, connectionToBeReached, positionType: point },
      title: 'My location',
      subtitle: location.subLocality,
      useContentFormatter,
      displayPriority: 0,
    };
  }

  function getLocationMarkerFormatter(
    location: Location,
    isCarTrip = false
  ): MarkerType {
    if (location.markerFormatter) {
      return location.markerFormatter as MarkerType;
    }

    return isCarTrip ? MarkerType.GeoLocation : MarkerType.Location;
  }

  function updateSearchByMapMovement(value: boolean): void {
    searchByMapMovement.value = value;
  }

  function mapViewChanged(event: MapMovedEvent): void {
    setMapMovedAndCreateNewPolygon(event);
  }

  function isMarkerInStartRectangleMapView(
    markerCoordinates: Coordinate
  ): boolean {
    return (
      markerCoordinates.latitude < DEFAULT_RECTANGLE.topLeft.latitude &&
      markerCoordinates.latitude > DEFAULT_RECTANGLE.bottomRight.latitude &&
      markerCoordinates.longitude > DEFAULT_RECTANGLE.topLeft.longitude &&
      markerCoordinates.longitude < DEFAULT_RECTANGLE.bottomRight.longitude
    );
  }

  function setDynamicMapIntegration(integration: Map): void {
    isFirstMapRendered.value = false;
    isMapCreated.value = true;
    setMapIntegration(integration);
  }

  function openFullScreenDynamicTripMap(): void {
    statsigEventsStore.logTripOverviewOpenFullScreenMap();
    shouldShowFullScreenMap.value = true;
  }

  function closeFullScreenDynamicTripMap(): void {
    shouldShowFullScreenMap.value = false;
    mapShouldCenterOnMarkers.value = true;
  }

  function setMapTypeId(newValue: MapTypeId): void {
    mapTypeId.value = newValue;
  }

  function updateMapShouldCenterOnMarkers(value: boolean): void {
    mapShouldCenterOnMarkers.value = value;
  }

  function setPointLocationFromPosition(
    position: Coordinate,
    pointType: MyPositionType,
    markerFormatter?: MarkerType
  ): void {
    const criteria = {
      position,
    };

    setPointGeoLocation(criteria, pointType, markerFormatter);
  }

  async function setPointGeoLocation(
    criteria: LocationCriteria,
    pointType: MyPositionType,
    markerFormatter?: MarkerType
  ): Promise<void> {
    try {
      criteria.language = mapLanguageLocale;
      if (criteria.position) {
        setPointLocation(pointType, {
          position: criteria.position,
          formattedAddress: t('planner.your_position'),
          markerFormatter,
        });
      } else {
        const location = await geocodingService.getLocation(criteria);
        setPointLocation(pointType, location);
      }
    } catch {
      captureException(
        new Error(
          `Error while getting location with props ${JSON.stringify(criteria)}`
        )
      );
    }
  }

  function setPointLocation(
    pointType: MyPositionType,
    location?: Location
  ): void {
    if (pointType === MyPositionType.Start) {
      startingLocation.value = location;

      tripStore.updateEndLocationIsManuallyTyped(
        tripStore.startingLocationIsManuallyTyped
      );
    } else if (pointType === MyPositionType.End) {
      endLocation.value = location;
    }
  }

  function setMapIntegration(integration: Map): void {
    if (!mapIntegration.value) {
      mapIntegration.value = integration;
    }
  }

  function setMapMovedAndCreateNewPolygon(event: MapMovedEvent): void {
    mapMovedByUser.value = event.userInitiated;
    if (editingIngredientStore.editingStayIngredient) {
      polygonActivityConstraint.value = new PolygonConstraint(
        createPolygonFromRectangle(event.newView)
      );
    }

    if (
      !editingIngredientStore.editingStayIngredient ||
      !event.userInitiated ||
      !searchByMapMovement.value
    ) {
      return;
    }

    polygonHotelConstraint.value = new PolygonConstraint(
      createPolygonFromRectangle(event.newView)
    );

    mapViews.next(polygonHotelConstraint.value);
  }

  function setCalculatedRoute(route: RouteWithDetails): void {
    calculatedRoute.value = route;
  }

  function getRoute(fromMarkers: Marker[]): Route {
    const newRoute: Route = [];
    for (let index = 0; index < fromMarkers.length - 1; index++) {
      if (fromMarkers[index + 1]?.data?.connectionToBeReached) {
        const currentMarker = fromMarkers[index];
        assertValue(currentMarker, 'mapState.ts > getRoute > currentMarker');
        const nextMarker = fromMarkers[index + 1];
        assertValue(nextMarker, 'mapState.ts > getRoute > nextMarker');

        newRoute.push(getRouteBetweenStops(currentMarker, nextMarker));
      }
    }
    return newRoute;
  }

  function getRouteBetweenStops(
    currentStop: Marker,
    nextStop: Marker
  ): RouteConnection {
    return {
      from: currentStop,
      to: nextStop,
      type: getConnectionType(nextStop),
    };
  }

  function getMarkersWithTransport(markers: Marker[]): Marker[] {
    return markers.map((destination) => {
      if (
        destination.data?.connectionToBeReached &&
        isBookableTransport(destination.data.connectionToBeReached)
      ) {
        return destination;
      }

      const data = {
        ...destination.data,
        connectionToBeReached:
          recipeStore.transportsByIngredient[destination.data?.ingredientId] ??
          0,
      };

      return {
        ...destination,
        data,
      };
    });
  }

  async function getPointGeolocationFromPlaceId(
    placeId: PlaceId
  ): Promise<Location | void> {
    const criteria: LocationCriteria = {
      place: placeId,
      language: mapLanguageLocale,
    };
    try {
      return await geocodingService.getLocation(criteria);
    } catch {
      captureException(
        new Error(
          `Error while getting location from placeId with props ${JSON.stringify(
            criteria
          )}`
        )
      );
    }
  }

  function createMarkerData(
    hotel: OverviewDestinationStaticInfo | DestinationStaticInfo,
    isDestination = false,
    isLocation = false
  ): MarkerData {
    return {
      hotelCode: hotel.hotelCode,
      ingredientId: hotel.ingredientId,
      typeOfProduct: hotel.typeOfHotel,
      isDestination,
      isLocation,
      semboSunRating: hotel.semboSunRating,
    };
  }

  function createOwnDestinationMarker(
    ownStay: OwnStayIngredientResponseWithStaticInfo
  ): Marker {
    const ownStayIngredient = recipeStore.getIngredient(
      ownStay.ingredientId
    ) as OwnStayIngredient;
    return {
      key: ownStayIngredient.ingredientId,
      position: {
        longitude: ownStayIngredient.positionFromSearchText?.longitude || 0,
        latitude: ownStayIngredient.positionFromSearchText?.latitude || 0,
      },
      subtitle: ownStayIngredient.displayText,
      title: itineraryStore.getRangeDays(ownStayIngredient.ingredientId),
      useContentFormatter: MarkerType.Location,
      data: {
        isDestination: true,
        isLocation: false,
        hotelCode: '',
        ingredientId: ownStayIngredient.ingredientId,
        typeOfProduct: AccommodationType.Hotel,
      },
      displayPriority: 2,
    };
  }

  function createDetailsAccommodationMarker(
    hotel: OverviewDestinationStaticInfo
  ): Marker {
    const ingredient = recipeStore.getHotelIngredient(hotel.ingredientId);

    return {
      key: hotel.hotelCode + hotel.ingredientId,
      position: {
        longitude: hotel.position.longitude.decimalDegrees,
        latitude: hotel.position.latitude.decimalDegrees,
      },
      subtitle: ingredient.displayText,
      title: itineraryStore.getRangeDays(ingredient.ingredientId),
      useContentFormatter: MarkerType.DetailsAccommodation,
      data: createMarkerData(hotel, true),
      displayPriority: 2,
    };
  }

  function createTransportMarkers(
    transportIngredient: AvailableTransportIngredientResponseWithStaticInfo
  ): [Marker, Marker] | [] {
    const ports = transportIngredient.staticInfo.ports;
    let connection: ConnectionType | undefined;
    let markerType: MarkerType | undefined;

    if (isFerryIngredientResponse(transportIngredient)) {
      connection = ConnectionType.FERRY;
      markerType = MarkerType.Ferry;
    }

    if (isFlightIngredientResponse(transportIngredient)) {
      connection = ConnectionType.FLIGHT;
      markerType = MarkerType.Flight;
    }

    if (ports) {
      const departurePort = ports[transportIngredient.departureIata];
      assertValue(
        departurePort,
        'mapState.ts > createTransportMarkers > departurePort'
      );

      const departureMarker: Marker = {
        key:
          transportIngredient.addToBasketToken +
          transportIngredient.departureIata,
        position: departurePort.position,
        data: {
          ingredientId: transportIngredient.ingredientId,
        },
        useContentFormatter: markerType,
        displayPriority: 1,
      };

      const arrivalPort = ports[transportIngredient.arrivalIata];
      assertValue(
        arrivalPort,
        'mapState.ts > createTransportMarkers > arrivalPort'
      );

      const arrivalMarker: Marker = {
        key:
          transportIngredient.addToBasketToken +
          transportIngredient.arrivalIata,
        position: arrivalPort.position,
        data: {
          ingredientId: transportIngredient.ingredientId,
          connectionToBeReached: connection,
        },
        useContentFormatter: markerType,
        displayPriority: 1,
      };

      return [departureMarker, arrivalMarker];
    }

    return [];
  }

  // Main computed properties and observables
  const markers = computed<Marker[]>(() => {
    if (editingIngredientStore.editingStayIngredient) {
      return [
        ...destinationProductMarkers.value,
        ...destinationMarkers.value.filter(
          (marker) =>
            marker.data.ingredientId ===
            editingIngredientStore.editingStayIngredient?.ingredientId
        ),
      ];
    }

    if (editingIngredientStore.editingTransportIngredient) {
      return transportProductMarkers.value;
    }

    return destinationMarkers.value;
  });

  const isMapLoading = computed(
    () =>
      !isMapCreated.value ||
      (calculatedRoute.value.length === 0 &&
        (configurationPanelStore.editTripCarDetails?.segments.length ?? 0) > 2)
  );

  const transportProductMarkers = computed<Marker[]>(() => {
    const transportIngredient =
      editingIngredientStore.editingTransportIngredient;

    if (
      !transportIngredient ||
      !transportIngredient.fromPort ||
      !transportIngredient.toPort
    ) {
      return [];
    }

    const fromPortPosition = tripStore.getPortStaticInfo(
      transportIngredient.fromPort,
      transportIngredient.ingredientType
    );

    const toPortPosition = tripStore.getPortStaticInfo(
      transportIngredient.toPort,
      transportIngredient.ingredientType
    );

    if (!fromPortPosition || !toPortPosition) {
      return [];
    }

    const connection: ConnectionType = isFlightIngredient(transportIngredient)
      ? ConnectionType.FLIGHT
      : ConnectionType.FERRY;

    const markerType: MarkerType = isFlightIngredient(transportIngredient)
      ? MarkerType.Flight
      : MarkerType.Ferry;

    const fromPortMarker: Marker = {
      key: transportIngredient.ingredientId + transportIngredient.fromPort,
      position: fromPortPosition.position,
      data: {
        ingredientId: transportIngredient.ingredientId,
      },
      useContentFormatter: markerType,
      displayPriority: 1,
    };

    const toPortMarker: Marker = {
      key: transportIngredient.ingredientId + transportIngredient.toPort,
      position: toPortPosition.position,
      data: {
        ingredientId: transportIngredient.ingredientId,
        connectionToBeReached: connection,
      },
      useContentFormatter: markerType,
      displayPriority: 1,
    };

    return [fromPortMarker, toPortMarker];
  });

  const destinationMarkers = computed<Marker[]>(() => {
    const markers = [...tripMarkers.value];

    if (
      startingLocationMarker.value &&
      recipeStore.startingTransportExists &&
      markers.length > 0
    ) {
      markers.unshift(startingLocationMarker.value);
    }

    if (
      endLocationMarker.value &&
      recipeStore.endingTransportExists &&
      markers.length > 0
    ) {
      markers.push(endLocationMarker.value);
    }

    return markers;
  });

  const mapRoute = computed<Route>(() => {
    if (editingIngredientStore.editingTransportIngredient) {
      return getRoute(transportProductMarkers.value);
    }

    const allIngredientsHaveMarkers =
      uniqBy(tripMarkers.value, (marker) => marker.data.ingredientId).length ===
      recipeStore.recipeWithoutOwnTransport.length;
    if (
      editingIngredientStore.editingStayIngredient ||
      !allIngredientsHaveMarkers
    ) {
      return [];
    }

    const destinationMarkersWithTransport = getMarkersWithTransport(
      tripMarkers.value
    );
    const lastRecipeIngredient = recipeStore.recipe.at(-1);
    const isThereNoReturnTransport =
      lastRecipeIngredient &&
      isOwnTransportIngredient(lastRecipeIngredient) &&
      lastRecipeIngredient.ownTransportType === TransportType.None;

    const thereIsNoTransport =
      !destinationMarkersWithTransport.some(
        (marker) => marker.data.connectionToBeReached
      ) && isThereNoReturnTransport;
    if (thereIsNoTransport) {
      return [];
    }

    const routeMarkers = [...destinationMarkersWithTransport];
    if (startingLocation.value) {
      const startingLocationTransportType =
        destinationMarkersWithTransport[0]?.data.connectionToBeReached;

      routeMarkers.unshift(
        createLocationMarker(
          MyPositionType.Start,
          startingLocation.value,
          startingLocationTransportType
        )
      );
    }

    if (endLocation.value) {
      const returnLocationTransportType =
        lastRecipeIngredient && isOwnTransportIngredient(lastRecipeIngredient)
          ? getConnectionTypeFromOwnTransportType(
              lastRecipeIngredient.ownTransportType
            )
          : undefined;
      routeMarkers.push(
        createLocationMarker(
          MyPositionType.End,
          endLocation.value,
          returnLocationTransportType
        )
      );
    }
    return getRoute(routeMarkers);
  });

  const destinationProductMarkers = computed<Marker[]>(() => {
    const result: Marker[] = [];

    tripStore.destinationProducts.forEach((product) => {
      if (!product.totalPrice) {
        return;
      }

      const availabilityPriceText = globalCurrencyStore.formatMoney(
        product.totalPrice.amount,
        product.totalPrice.currency
      );

      const isTicket = isTicketActivity(product.typeOfHotel);
      const isGolf = isGolfActivity(product.typeOfHotel);

      const getContentFormatter = (): MarkerType => {
        if (isTicket) {
          return MarkerType.Activity;
        }
        if (isGolf) {
          return MarkerType.Golf;
        }
        return MarkerType.Hotel;
      };

      const marker: Marker = {
        key: `${product.hotelCode}-${availabilityPriceText}`,
        position: {
          latitude: product.position.latitude.decimalDegrees,
          longitude: product.position.longitude.decimalDegrees,
        },
        data: {
          ...createMarkerData(product),
          formattedPrice: availabilityPriceText,
        },
        useContentFormatter: getContentFormatter(),
        useClusterContentFormatter: getContentFormatter(),
      };

      let formattedMarker: Marker = {
        ...marker,
        cssClass: '',
      };

      if (isTicket || isGolf) {
        formattedMarker = {
          ...formattedMarker,
          title: product.name,
        };
      }

      if (
        editingIngredientStore.editingStayIngredient &&
        isHotelIngredient(editingIngredientStore.editingStayIngredient) &&
        editingIngredientStore.editingStayIngredient.heartedHotelCodes.includes(
          marker.data?.hotelCode
        )
      ) {
        formattedMarker = {
          ...formattedMarker,
          cssClass: 'hearted',
          displayPriority: 2,
        };
      }

      if (marker.data?.hotelCode === tripStore.preselectedHotel?.hotelCode) {
        formattedMarker = {
          ...formattedMarker,
          cssClass: `${formattedMarker.cssClass} preselected`,
          displayPriority: 3,
        };
      }

      result.push(formattedMarker);
    });

    return result;
  });

  const mapProperties = computed<MapProperties>(() => {
    const markersWithUserLocation =
      startingLocationMarker.value !== undefined && markers.value.length === 0
        ? [startingLocationMarker.value]
        : markers.value;
    const destinationMarkers = getPrimaryMapMarkers(markersWithUserLocation);
    const startRectangle =
      destinationMarkers.length === 0 ? DEFAULT_RECTANGLE : undefined;

    return {
      markers: destinationMarkers,
      markerFormatters: getMarkerFormatters(mapTypeId.value),
      route: mapRoute.value,
      showAllMarkers: mapShouldCenterOnMarkers.value,
      startRectangle,
      gestureHandling: 'greedy',
      locale: mapLanguageLocale,
      addMarkersBetweenRoutes: true,
    };
  });

  // RxJS observables
  const newDestinationMarkers = asObservable(
    computed(() => tripStore.selectedItineraryWithStaticInfo)
  ).pipe(
    distinctUntilChanged(
      (previousItinerary, currentItinerary) =>
        stringComparer(previousItinerary ?? '', currentItinerary ?? '') === 0
    ),
    throttleTime(throttleTimeMiliseconds, undefined, {
      leading: true,
      trailing: true,
    }),
    map((itineraryWithPrice) => {
      const ingredientsInfo = itineraryWithPrice?.itinerary.filter(
        (ingredientResponseWithStaticInfo) =>
          !!ingredientResponseWithStaticInfo.staticInfo ||
          isOwnStayIngredientResponseWithStaticInfo(
            ingredientResponseWithStaticInfo
          )
      );
      return ingredientsInfo?.length ===
        itineraryWithPrice?.itinerary.filter(
          (itinerary) =>
            !isOwnTransportIngredientResponseWithStaticInfo(itinerary)
        ).length
        ? ingredientsInfo
        : [];
    }),
    map((ingredientResponsesWithStaticInfo) => {
      if (!ingredientResponsesWithStaticInfo) {
        return [];
      }

      return ingredientResponsesWithStaticInfo.reduce(
        (markers: Marker[], response) => {
          if (isAvailableTransportIngredientResponseWithStaticInfo(response)) {
            markers.push(...createTransportMarkers(response));
          }

          if (isAvailableHotelIngredientResponseWithStaticInfo(response)) {
            markers.push(createDetailsAccommodationMarker(response.staticInfo));
          }

          if (isOwnStayIngredientResponseWithStaticInfo(response)) {
            markers.push(createOwnDestinationMarker(response));
          }

          return markers;
        },
        []
      );
    }),
    distinctUntilChanged(
      (previousMarker, currentMarker) =>
        previousMarker.map((marker) => marker.key).join(',') ===
        currentMarker.map((marker) => marker.key).join(',')
    ),
    logged('new markers'),
    share()
  );

  newDestinationMarkers
    .pipe(logged('setting destination markers'))
    .subscribe((markers) => {
      tripMarkers.value = markers;
    });

  watch(mapRoute, (route) => {
    isLoadingCalculatedRoute.value = true;
    setCalculatedRoute([]);

    directionsService
      .calculateRoute(route)
      .then(setCalculatedRoute)
      .finally(() => (isLoadingCalculatedRoute.value = false));
  });

  // Export store interface
  return {
    // State
    isFirstMapRendered: readonlyRef(isFirstMapRendered),
    isMapCreated: readonlyRef(isMapCreated),
    shouldShowFullScreenMap: readonlyRef(shouldShowFullScreenMap),
    isMapLoading: readonlyRef(isMapLoading),
    mapTypeId: readonlyRef(mapTypeId),
    calculatedRoute: readonlyRef(calculatedRoute),
    isLoadingCalculatedRoute: readonlyRef(isLoadingCalculatedRoute),
    searchByMapMovement: readonlyRef(searchByMapMovement),
    mapShouldCenterOnMarkers: readonlyRef(mapShouldCenterOnMarkers),
    endLocation: readonlyRef(endLocation),
    startingLocation: readonlyRef(startingLocation),
    DEFAULT_RECTANGLE,

    // Actions
    mapViewChanged,
    isMarkerInStartRectangleMapView,
    setDynamicMapIntegration,
    openFullScreenDynamicTripMap,
    closeFullScreenDynamicTripMap,
    setMapTypeId,
    setPointLocationFromPosition,
    updateMapShouldCenterOnMarkers,
    updateSearchByMapMovement,
    setMapIntegration,
    setMapMovedAndCreateNewPolygon,
    getPointGeolocationFromPlaceId,
    setPointLocation,

    mapProperties,
    markers,
    mapRoute,
    mapViews: mapViews.pipe(debounceTime(mapViewDebounceTime)),
  };
});
